import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

/* ──────────────────────────────────────────────────────────────────
   DigitalTwin · compact card
   "We run a live digital twin of your BESS."
   Left  = physical BESS (slow sensor scan)
   Mid   = wires + ports that visually connect physical ⟷ twin
   Right = the live twin (scanning cell grid + brief data-in flash)
   Strip = 6 live KPIs
   ────────────────────────────────────────────────────────────────── */

const N = 24; // 4 racks × 6 modules — kept tight so the section stays small
const CELL_EFF = (() => {
  let s = 13; const a = [];
  for (let i = 0; i < N; i++) { s = (s * 1103515245 + 12345) & 0x7fffffff; a.push(97.8 + (s / 0x7fffffff) * 1.6); }
  a[7] = 97.4; a[16] = 97.5;
  return a;
})();
const WATCH = new Set([7, 16]);
const cellId = (i) => `R${Math.floor(i / 6) + 1}-M${String((i % 6) + 1).padStart(2, '0')}`;

const now = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function DigitalTwin() {
  const ref = useRef(null);
  const live = useInView(ref, { margin: "-15% 0px" });
  const [scan, setScan] = useState(0);           // twin · continuous sweep
  const [physRead, setPhysRead] = useState(3);   // physical · sensor read pings
  const [clock, setClock] = useState(now());

  // twin continuous scan — same cadence as a 240ms sync tick
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setScan(s => (s + 1) % N);
    }, 240);
    return () => clearInterval(id);
  }, [live]);

  // physical sensor reads — slower, randomised, looks like real telemetry polling
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setPhysRead(Math.floor(Math.random() * N));
    }, 720);
    return () => clearInterval(id);
  }, [live]);

  useEffect(() => {
    const id = setInterval(() => setClock(now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const sWatch = WATCH.has(scan);
  const sEff = CELL_EFF[scan];

  return (
    <div ref={ref} className="twc" data-testid="digital-twin">
      {/* Header */}
      <div className="twc-hd">
        <span className="twc-live-dot" />
        <span className="twc-hd-title">LIVE DIGITAL TWIN · MIRRORED EVERY 15 MIN</span>
        <span className="twc-hd-clock">{clock} IST</span>
      </div>

      {/* Pair: physical | wires+ports | twin */}
      <div className="twc-pair">
        {/* Left · physical (with sensor-read pings) */}
        <div className="twc-side twc-side-phys" data-testid="twc-physical">
          <div className="twc-side-tag">PHYSICAL</div>
          <div className="twc-rack-wrap">
            <div className="twc-rack">
              <div className="twc-rack-frame">
                <div className="twc-rack-body">
                  {[...Array(N)].map((_, i) => (
                    <div
                      key={i}
                      className={`twc-rack-cell ${i === physRead ? 'twc-rack-cell-read' : ''}`}
                      style={{ animationDelay: `${(i % 5) * 0.42}s` }}
                    />
                  ))}
                </div>
                <div className="twc-rack-tip" />
              </div>
              <div className="twc-rack-base" />
            </div>
            {/* Connection port — visually meets the wires */}
            <div className="twc-port twc-port-right" aria-hidden="true">
              <span className="twc-port-dot" />
            </div>
          </div>
          <div className="twc-side-meta">YOUR BESS · 1 MW / 4 MWh · LFP</div>
        </div>

        {/* Middle · Gemini-style flowing currents + PowerBESS.ai brain pill */}
        <div className="twc-link">
          <div className="twc-link-wires">
            {/* Desktop horizontal flow */}
            <svg className="twc-link-wires-h" viewBox="0 350 1440 340" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <filter id="twcGemBlur" x="-10%" y="-10%" width="120%" height="120%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
                </filter>
              </defs>

              {/* Background glow layer · gaussian-blurred paths */}
              <g opacity="0.85">
                <path d="M0 663C145.5 663 191 666.265 269 647C326.5 630 339.5 621 397.5 566C439 531.5 455 529.5 490 523C509.664 519.348 521 503.736 538 504.236C553.591 504.236 562.429 514.739 584.66 522.749C592.042 525.408 600.2 526.237 607.356 523.019C624.755 515.195 641.446 496.324 657 496.735C673.408 496.735 693.545 519.572 712.903 526.769C718.727 528.934 725.184 528.395 730.902 525.965C751.726 517.115 764.085 497.106 782 496.735C794.831 496.47 804.103 508.859 822.469 518.515C835.13 525.171 850.214 526.815 862.827 520.069C875.952 513.049 889.748 502.706 903.5 503.736C922.677 505.171 935.293 510.562 945.817 515.673C954.234 519.76 963.095 522.792 972.199 524.954C996.012 530.611 1007.42 534.118 1034 549C1077.5 573.359 1082.5 594.5 1140 629C1206 670 1328.5 662.5 1440 662.5"
                      stroke="#88AA00" strokeWidth="3" fill="none" filter="url(#twcGemBlur)" />
                <path d="M0 587.5C147 587.5 277 587.5 310 573.5C348 563 392.5 543.5 408 535C434 523.5 426 526.235 479 515.235C494 512.729 523 510.435 534.5 512.735C554.5 516.735 555.5 523.235 576 523.735C592 523.735 616 496.735 633 497.235C648.671 497.235 661.31 515.052 684.774 524.942C692.004 527.989 700.2 528.738 707.349 525.505C724.886 517.575 741.932 498.33 757.5 498.742C773.864 498.742 791.711 520.623 810.403 527.654C816.218 529.841 822.661 529.246 828.451 526.991C849.246 518.893 861.599 502.112 879.5 501.742C886.47 501.597 896.865 506.047 907.429 510.911C930.879 521.707 957.139 519.639 982.951 520.063C1020.91 520.686 1037.5 530.797 1056.5 537C1102.24 556.627 1116.5 570.704 1180.5 579.235C1257.5 589.5 1279 587 1440 588"
                      stroke="#A8D900" strokeWidth="3" fill="none" filter="url(#twcGemBlur)" />
                <path d="M0 514C147.5 514.333 294.5 513.735 380.5 513.735C405.976 514.94 422.849 515.228 436.37 515.123C477.503 514.803 518.631 506.605 559.508 511.197C564.04 511.706 569.162 512.524 575 513.735C588 516.433 616 521.702 627.5 519.402C647.5 515.402 659 499.235 680.5 499.235C700.5 499.235 725 529.235 742 528.735C757.654 528.735 768.77 510.583 791.793 500.59C798.991 497.465 807.16 496.777 814.423 499.745C832.335 507.064 850.418 524.648 866 524.235C882.791 524.235 902.316 509.786 921.814 505.392C926.856 504.255 932.097 504.674 937.176 505.631C966.993 511.248 970.679 514.346 989.5 514.735C1006.3 515.083 1036.5 513.235 1055.5 513.235C1114.5 513.235 1090.5 513.235 1124 513.235C1177.5 513.235 1178.99 514.402 1241 514.402C1317.5 514.402 1274.5 512.568 1440 513.235"
                      stroke="#CCFF00" strokeWidth="3" fill="none" filter="url(#twcGemBlur)" />
                <path d="M0 438.5C150.5 438.5 261 438.318 323.5 456.5C351 464.5 387.517 484.001 423.5 494.5C447.371 501.465 472 503.735 487 507.735C503.786 512.212 504.5 516.808 523 518.735C547 521.235 564.814 501.235 584.5 501.235C604.5 501.235 626 529.069 643 528.569C658.676 528.569 672.076 511.63 695.751 501.972C703.017 499.008 711.231 498.208 718.298 501.617C735.448 509.889 751.454 529.98 767 529.569C783.364 529.569 801.211 507.687 819.903 500.657C825.718 498.469 832.141 499.104 837.992 501.194C859.178 508.764 873.089 523.365 891 523.735C907.8 524.083 923 504.235 963 506.735C1034.5 506.735 1047.5 492.68 1071 481.5C1122.5 457 1142.23 452.871 1185 446.5C1255.5 436 1294 439 1439.5 439"
                      stroke="#DBFF40" strokeWidth="3" fill="none" filter="url(#twcGemBlur)" />
                <path d="M0.5 364C145.288 362.349 195 361.5 265.5 378C322 391.223 399.182 457.5 411 467.5C424.176 478.649 456.916 491.677 496.259 502.699C498.746 503.396 501.16 504.304 503.511 505.374C517.104 511.558 541.149 520.911 551.5 521.236C571.5 521.236 590 498.736 611.5 498.736C631.5 498.736 652.5 529.236 669.5 528.736C685.171 528.736 697.81 510.924 721.274 501.036C728.505 497.988 736.716 497.231 743.812 500.579C761.362 508.857 778.421 529.148 794 528.736C810.375 528.736 829.35 508.68 848.364 502.179C854.243 500.169 860.624 500.802 866.535 502.718C886.961 509.338 898.141 519.866 916 520.236C932.8 520.583 934.5 510.236 967.5 501.736C1011.5 491 1007.5 493.5 1029.5 480C1069.5 453.5 1072 440.442 1128.5 403.5C1180.5 369.5 1275 360.374 1439 364"
                      stroke="#F5FFB0" strokeWidth="3" fill="none" filter="url(#twcGemBlur)" />
              </g>

              {/* Foreground · current flowing along the path */}
              <g>
                <path d="M0 663C145.5 663 191 666.265 269 647C326.5 630 339.5 621 397.5 566C439 531.5 455 529.5 490 523C509.664 519.348 521 503.736 538 504.236C553.591 504.236 562.429 514.739 584.66 522.749C592.042 525.408 600.2 526.237 607.356 523.019C624.755 515.195 641.446 496.324 657 496.735C673.408 496.735 693.545 519.572 712.903 526.769C718.727 528.934 725.184 528.395 730.902 525.965C751.726 517.115 764.085 497.106 782 496.735C794.831 496.47 804.103 508.859 822.469 518.515C835.13 525.171 850.214 526.815 862.827 520.069C875.952 513.049 889.748 502.706 903.5 503.736C922.677 505.171 935.293 510.562 945.817 515.673C954.234 519.76 963.095 522.792 972.199 524.954C996.012 530.611 1007.42 534.118 1034 549C1077.5 573.359 1082.5 594.5 1140 629C1206 670 1328.5 662.5 1440 662.5"
                      stroke="#88AA00" strokeWidth="2.5" strokeLinecap="round" fill="none"
                      pathLength={1} strokeDasharray="0.22 0.78">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.2s" repeatCount="indefinite" />
                </path>
                <path d="M0 587.5C147 587.5 277 587.5 310 573.5C348 563 392.5 543.5 408 535C434 523.5 426 526.235 479 515.235C494 512.729 523 510.435 534.5 512.735C554.5 516.735 555.5 523.235 576 523.735C592 523.735 616 496.735 633 497.235C648.671 497.235 661.31 515.052 684.774 524.942C692.004 527.989 700.2 528.738 707.349 525.505C724.886 517.575 741.932 498.33 757.5 498.742C773.864 498.742 791.711 520.623 810.403 527.654C816.218 529.841 822.661 529.246 828.451 526.991C849.246 518.893 861.599 502.112 879.5 501.742C886.47 501.597 896.865 506.047 907.429 510.911C930.879 521.707 957.139 519.639 982.951 520.063C1020.91 520.686 1037.5 530.797 1056.5 537C1102.24 556.627 1116.5 570.704 1180.5 579.235C1257.5 589.5 1279 587 1440 588"
                      stroke="#A8D900" strokeWidth="2.5" strokeLinecap="round" fill="none"
                      pathLength={1} strokeDasharray="0.2 0.8">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.6s" repeatCount="indefinite" begin="0.25s" />
                </path>
                <path d="M0 514C147.5 514.333 294.5 513.735 380.5 513.735C405.976 514.94 422.849 515.228 436.37 515.123C477.503 514.803 518.631 506.605 559.508 511.197C564.04 511.706 569.162 512.524 575 513.735C588 516.433 616 521.702 627.5 519.402C647.5 515.402 659 499.235 680.5 499.235C700.5 499.235 725 529.235 742 528.735C757.654 528.735 768.77 510.583 791.793 500.59C798.991 497.465 807.16 496.777 814.423 499.745C832.335 507.064 850.418 524.648 866 524.235C882.791 524.235 902.316 509.786 921.814 505.392C926.856 504.255 932.097 504.674 937.176 505.631C966.993 511.248 970.679 514.346 989.5 514.735C1006.3 515.083 1036.5 513.235 1055.5 513.235C1114.5 513.235 1090.5 513.235 1124 513.235C1177.5 513.235 1178.99 514.402 1241 514.402C1317.5 514.402 1274.5 512.568 1440 513.235"
                      stroke="#CCFF00" strokeWidth="2.5" strokeLinecap="round" fill="none"
                      pathLength={1} strokeDasharray="0.18 0.82">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.0s" repeatCount="indefinite" begin="0.5s" />
                </path>
                <path d="M0 438.5C150.5 438.5 261 438.318 323.5 456.5C351 464.5 387.517 484.001 423.5 494.5C447.371 501.465 472 503.735 487 507.735C503.786 512.212 504.5 516.808 523 518.735C547 521.235 564.814 501.235 584.5 501.235C604.5 501.235 626 529.069 643 528.569C658.676 528.569 672.076 511.63 695.751 501.972C703.017 499.008 711.231 498.208 718.298 501.617C735.448 509.889 751.454 529.98 767 529.569C783.364 529.569 801.211 507.687 819.903 500.657C825.718 498.469 832.141 499.104 837.992 501.194C859.178 508.764 873.089 523.365 891 523.735C907.8 524.083 923 504.235 963 506.735C1034.5 506.735 1047.5 492.68 1071 481.5C1122.5 457 1142.23 452.871 1185 446.5C1255.5 436 1294 439 1439.5 439"
                      stroke="#DBFF40" strokeWidth="2.5" strokeLinecap="round" fill="none"
                      pathLength={1} strokeDasharray="0.2 0.8">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.4s" repeatCount="indefinite" begin="0.75s" />
                </path>
                <path d="M0.5 364C145.288 362.349 195 361.5 265.5 378C322 391.223 399.182 457.5 411 467.5C424.176 478.649 456.916 491.677 496.259 502.699C498.746 503.396 501.16 504.304 503.511 505.374C517.104 511.558 541.149 520.911 551.5 521.236C571.5 521.236 590 498.736 611.5 498.736C631.5 498.736 652.5 529.236 669.5 528.736C685.171 528.736 697.81 510.924 721.274 501.036C728.505 497.988 736.716 497.231 743.812 500.579C761.362 508.857 778.421 529.148 794 528.736C810.375 528.736 829.35 508.68 848.364 502.179C854.243 500.169 860.624 500.802 866.535 502.718C886.961 509.338 898.141 519.866 916 520.236C932.8 520.583 934.5 510.236 967.5 501.736C1011.5 491 1007.5 493.5 1029.5 480C1069.5 453.5 1072 440.442 1128.5 403.5C1180.5 369.5 1275 360.374 1439 364"
                      stroke="#F5FFB0" strokeWidth="2.5" strokeLinecap="round" fill="none"
                      pathLength={1} strokeDasharray="0.22 0.78">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.8s" repeatCount="indefinite" begin="1s" />
                </path>
              </g>
            </svg>

            {/* Mobile vertical flow — connects physical (above) ⟷ digital twin (below) */}
            <svg className="twc-link-wires-v" viewBox="0 0 200 320" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <filter id="twcGemBlurV" x="-10%" y="-10%" width="120%" height="120%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
                </filter>
              </defs>
              {/* Background blurred glow */}
              <g opacity="0.85">
                <path d="M50 0 C 80 80, 120 130, 100 160 C 80 200, 130 260, 70 320" stroke="#88AA00" strokeWidth="2.5" fill="none" filter="url(#twcGemBlurV)" />
                <path d="M75 0 C 95 90, 95 140, 100 160 C 105 195, 115 270, 130 320" stroke="#A8D900" strokeWidth="2.5" fill="none" filter="url(#twcGemBlurV)" />
                <path d="M100 0 C 100 100, 100 140, 100 160 C 100 195, 100 260, 100 320" stroke="#CCFF00" strokeWidth="2.5" fill="none" filter="url(#twcGemBlurV)" />
                <path d="M125 0 C 105 90, 105 140, 100 160 C 95 195, 85 270, 70 320" stroke="#DBFF40" strokeWidth="2.5" fill="none" filter="url(#twcGemBlurV)" />
                <path d="M150 0 C 120 80, 80 130, 100 160 C 120 200, 70 260, 130 320" stroke="#F5FFB0" strokeWidth="2.5" fill="none" filter="url(#twcGemBlurV)" />
              </g>
              {/* Foreground · current flowing top → bottom */}
              <g>
                <path d="M50 0 C 80 80, 120 130, 100 160 C 80 200, 130 260, 70 320" stroke="#88AA00" strokeWidth="2" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray="0.22 0.78">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.2s" repeatCount="indefinite" />
                </path>
                <path d="M75 0 C 95 90, 95 140, 100 160 C 105 195, 115 270, 130 320" stroke="#A8D900" strokeWidth="2" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray="0.2 0.8">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.6s" repeatCount="indefinite" begin="0.25s" />
                </path>
                <path d="M100 0 L 100 320" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray="0.18 0.82">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.0s" repeatCount="indefinite" begin="0.5s" />
                </path>
                <path d="M125 0 C 105 90, 105 140, 100 160 C 95 195, 85 270, 70 320" stroke="#DBFF40" strokeWidth="2" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray="0.2 0.8">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.4s" repeatCount="indefinite" begin="0.75s" />
                </path>
                <path d="M150 0 C 120 80, 80 130, 100 160 C 120 200, 70 260, 130 320" stroke="#F5FFB0" strokeWidth="2" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray="0.22 0.78">
                  <animate attributeName="stroke-dashoffset" from="1" to="0" dur="3.8s" repeatCount="indefinite" begin="1s" />
                </path>
              </g>
            </svg>

            {/* Center brain pill — POWERBESS AI works on the flow */}
            <div className="twc-link-brand">
              <span className="twc-link-brand-text">POWERBESS AI</span>
            </div>
          </div>
        </div>

        {/* Right · live twin */}
        <div className="twc-side twc-side-twin" data-testid="twc-twin">
          <div className="twc-side-tag" style={{ color: '#CCFF00' }}>DIGITAL TWIN</div>
          <div className="twc-cells-wrap">
            {/* Port that visually receives the east-bound wire */}
            <div className="twc-port twc-port-left" aria-hidden="true">
              <span className="twc-port-dot" />
            </div>
            <div className="twc-cells" data-testid="twc-cells">
              {CELL_EFF.map((_, i) => (
                <div
                  key={i}
                  className={`twc-cell ${i === scan ? 'twc-cell-scan' : ''}`}
                  style={i === scan ? {
                    background: sWatch ? '#FFB800' : '#CCFF00',
                    boxShadow: sWatch
                      ? '0 0 12px #FFB800cc, inset 0 0 4px #FFB800'
                      : '0 0 12px #CCFF00cc, inset 0 0 4px #CCFF00',
                  } : {
                    background: WATCH.has(i)
                      ? 'rgba(255,184,0,0.32)'
                      : `rgba(204,255,0,${(0.06 + ((CELL_EFF[i] - 97.8) / 1.6) * 0.28).toFixed(3)})`,
                    animationDelay: `${(i % 5) * 0.42}s`,
                  }} />
              ))}
            </div>
          </div>
          <div className="twc-side-meta">
            SCAN <b style={{ color: sWatch ? '#FFB800' : '#CCFF00' }}>{cellId(scan)}</b>
            <span className="twc-dot-sep">·</span>
            η <b>{sEff.toFixed(1)}%</b>
          </div>
        </div>
      </div>

      {/* KPI strip · 6 numbers */}
      <div className="twc-kpis" data-testid="twc-kpis">
        <div className="twc-kpi"><span>SoH</span><b style={{ color: '#CCFF00' }}>97.6%</b></div>
        <div className="twc-kpi"><span>EFC</span><b>412</b></div>
        <div className="twc-kpi"><span>RTE</span><b>87.2%</b></div>
        <div className="twc-kpi"><span>ΔT</span><b>2.1°C</b></div>
        <div className="twc-kpi"><span>Cycle cost</span><b>₹2,840</b></div>
        <div className="twc-kpi"><span>Twin RMSE</span><b style={{ color: '#CCFF00' }}>0.18%</b></div>
      </div>
    </div>
  );
}
